import { Card, CardContent, Link, Typography } from '@material-ui/core';
import { Check, Info } from '@material-ui/icons';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { Alert, Button, TextBox } from '@superdispatch/ui-lab';
import { Form, FormikProvider } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreditCard } from 'shared/errors/PaymentService';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { ChargebeeResponse } from 'shared/helpers/ChargebeeWeb';
import { filterSearchParams, useQuery } from 'shared/helpers/RoutingHelpers';
import { EncryptIcon } from 'shared/icons/EncryptIcon';
import { EncryptOutlinedIcon } from 'shared/icons/EncryptOutlinedIcon';
import { billingAddressFormErrorsGetter } from 'shared/modules/subscription/core/SubscriptionUtils';
import { trackSubscriptionEvent } from 'shared/modules/subscription/data/SubscriptionAnalytics';
import {
  useBillingAddress,
  useSubscriptionAPI,
  useSubscriptionDetails,
} from 'shared/modules/subscription/SubscriptionAPI';
import { subscriptionSchema } from 'shared/modules/subscription/SubscriptionDTO';
import { LocationState } from 'shared/modules/subscription/SubscriptionSalesTaxDrawer';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { ChargebeeEmptyPlaceholder } from 'shared/ui/ChargebeeEmptyPlaceholder';
import { SubscriptionForm } from './SubscriptionForm';

const validationSchema = toFormikValidationSchema(subscriptionSchema);

export function SubscriptionPaymentCard() {
  const navigate = useNavigate();
  const { data: settings } = useCarrierSettings();
  const [{ utm_medium, driver_guid, back_url }] = useQuery();
  const { data: subscriptionBillingAddress } = useBillingAddress();
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const clientToken: string = subscriptionDetails?.client_token || '';
  const { cardRef, isChargebeeAvailable, addCreditCard } =
    useCreditCard(clientToken);
  const [billingAddress, setBillingAddress] = useState<{
    zip: string;
    country: string;
    state: string;
  }>({ zip: '', country: '', state: '' });
  const { saveBillingAddress } = useSubscriptionAPI();
  const carrierGuid = settings?.carrier.guid || '';

  const formik = useFormikEnhanced({
    validateOnBlur: false,
    validationSchema,
    initialValues: {
      zip: subscriptionBillingAddress?.billing_zip || '',
      country: subscriptionBillingAddress?.billing_country || 'US',
      state: subscriptionBillingAddress?.billing_state || '',
      city: subscriptionBillingAddress?.billing_city || '',
      address: subscriptionBillingAddress?.billing_address || '',
    },
    getFormErrors: (error) => {
      return billingAddressFormErrorsGetter(error);
    },
    onSubmit: async (values) => {
      setBillingAddress(values);
      if (formik.dirty) {
        await saveBillingAddress({
          billing_country: values.country,
          billing_zip: values.zip,
          billing_state: values.state,
          billing_address: values.address,
          billing_city: values.city,
        });
      }
      return addCreditCard({});
    },
    onSubmitSuccess: (values: ChargebeeResponse) => {
      const {
        token,
        vaultToken,
        additional_information: { braintree },
      } = values;

      const locationState: LocationState = {
        token,
        vaultToken,
        billingAddress,
        cardDetails: braintree,
      };

      if (formik.dirty) {
        trackSubscriptionEvent({
          name: 'Carrier Edited Subscription Billing Address',
          utm_medium,
          carrier_guid: carrierGuid,
          driver_guid,
        });
      }

      navigate(
        {
          pathname: '/settings/subscription/upgrade_plan',
          search: filterSearchParams({ utm_medium, driver_guid, back_url }),
        },
        { state: locationState },
      );
    },
  });

  return (
    <Card aria-label="Add Payment Information">
      <CardContent>
        <Stack space="medium">
          <Typography variant="h3">Add Payment Information</Typography>
          <Stack space="xsmall">
            <Columns space="xxsmall" align="top">
              <Column width="content">
                <EncryptIcon />
              </Column>
              <Column width="fluid">
                <TextBox color="secondary" variant="body">
                  We use industry-standard encryption and security protocols to
                  safeguard your information. Your card details are not stored
                  on our internal servers for your protection.
                </TextBox>
              </Column>
            </Columns>
            <Columns space="xxsmall" align="top">
              <Column width="content">
                <Check fontSize="small" htmlColor={ColorDynamic.Dark100} />
              </Column>
              <Column width="fluid">
                <TextBox color="secondary" variant="body">
                  We accept all major credit cards
                </TextBox>
              </Column>
            </Columns>
          </Stack>
          <FormikProvider value={formik}>
            <Form>
              <Stack space="large">
                {isChargebeeAvailable ? (
                  <SubscriptionForm ref={cardRef} />
                ) : (
                  <ChargebeeEmptyPlaceholder />
                )}
                <Stack space="small">
                  <Button
                    pending={formik.isSubmitting}
                    fullWidth={true}
                    startIcon={<EncryptOutlinedIcon />}
                    type="submit"
                    size="large"
                  >
                    Continue to Upgrade
                  </Button>
                  <Inline horizontalAlign="center">
                    <Typography
                      align="center"
                      color="textSecondary"
                      variant="caption"
                    >
                      By continuing, you agree to our{' '}
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.superdispatch.com/terms-of-service"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://support.superdispatch.com/hc/articles/35648690974739"
                      >
                        Refund Policy
                      </Link>
                    </Typography>
                  </Inline>

                  {formik.status.type === 'rejected' && (
                    <Alert
                      icon={
                        <Info
                          fontSize="small"
                          htmlColor={ColorDynamic.Red500}
                        />
                      }
                      severity="critical"
                    >
                      {formik.status.payload.message}
                    </Alert>
                  )}
                </Stack>
              </Stack>
            </Form>
          </FormikProvider>
        </Stack>
      </CardContent>
    </Card>
  );
}
